import { useEffect, useState } from "react";
import { WifiOff, RefreshCw, CloudUpload, AlertTriangle } from "lucide-react";
import { onOffline, syncPending, pendingSales, discardPending } from "../lib/offline";
import { money, dateFmt } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

export default function OfflineBadge() {
  const [st, setSt] = useState({ online: true, syncing: false, pending: 0, lastError: "" });
  const [open, setOpen] = useState(false);
  useEffect(() => onOffline(setSt), []);
  useEffect(() => {
    const done = () => toast.success("Ventas sin conexión sincronizadas");
    window.addEventListener("offline-synced", done);
    return () => window.removeEventListener("offline-synced", done);
  }, []);
  useEffect(() => { if (!st.online) toast.warning("Sin conexión · el POS sigue operando; las ventas se guardarán en este equipo", { id: "offline-warn" }); }, [st.online]);

  if (st.online && !st.syncing && st.pending === 0) return null;
  const cls = !st.online ? "bg-red-600 text-white" : st.syncing ? "bg-sky-600 text-white" : st.lastError ? "bg-amber-500 text-white" : "bg-amber-500 text-white";
  const label = !st.online ? `Sin conexión${st.pending ? ` · ${st.pending} venta${st.pending > 1 ? "s" : ""} pendiente${st.pending > 1 ? "s" : ""}` : ""}`
    : st.syncing ? "Sincronizando…" : `${st.pending} venta${st.pending > 1 ? "s" : ""} por sincronizar`;
  const Icon = !st.online ? WifiOff : st.syncing ? RefreshCw : CloudUpload;
  const list = pendingSales();
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`flex items-center gap-1.5 px-2.5 h-8 rounded-full text-xs font-bold ${cls} ${!st.online ? "animate-pulse" : ""}`} data-testid="offline-badge" title="Estado de conexión">
        <Icon size={14} className={st.syncing ? "animate-spin" : ""} /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="offline-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">{st.online ? <CloudUpload size={18} className="text-sky-600" /> : <WifiOff size={18} className="text-red-600" />} {st.online ? "Ventas por sincronizar" : "Modo sin conexión"}</DialogTitle>
            <DialogDescription>{st.online ? "Se envían automáticamente al servidor." : "Puedes seguir vendiendo. Las ventas se guardan en este equipo y se enviarán solas al volver internet."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {list.map((x) => (
              <div key={x.client_id} className="rounded-lg border border-border p-2.5 text-sm flex items-start justify-between gap-2" data-testid={`offline-sale-${x.client_id}`}>
                <div>
                  <div className="font-semibold">{x.sale.folio} · {money(x.sale.total)} <span className="text-xs text-muted-foreground uppercase">{x.sale.payment_method}</span></div>
                  <div className="text-[11px] text-muted-foreground">{dateFmt(x.created_at)} · {(x.sale.items || []).length} art.</div>
                  {x.error && <div className="text-[11px] text-red-600 flex items-center gap-1 mt-1"><AlertTriangle size={12} /> {x.error}</div>}
                </div>
                {x.error && <button type="button" className="text-[11px] text-red-500 underline shrink-0" onClick={() => { if (window.confirm("¿Descartar esta venta pendiente? No se registrará en el sistema.")) discardPending(x.client_id); }} data-testid={`offline-discard-${x.client_id}`}>Descartar</button>}
              </div>
            ))}
            {list.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">No hay ventas pendientes</div>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cerrar</Button>
            <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold" disabled={!st.online || st.syncing || !list.length} onClick={() => syncPending()} data-testid="offline-sync-now"><RefreshCw size={15} className={`mr-1 ${st.syncing ? "animate-spin" : ""}`} /> Sincronizar ahora</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
